import { useSubmitWebsiteUrlMutation } from '@dieterApi/scan/useSubmitWebsiteUrlMutation';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { Button } from '@components/ui/Button/Button';
import { Input } from '@components/ui/Input/Input';
import { Markdown } from '@components/ui/Markdown/Markdown';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UrlStep as Step } from '../../utils';
import { Props as BaseProps } from '../Step';

export type Props = BaseProps<Step>;

/**
 * Renders the synthetic website-URL question. On submit it stores the URL and
 * kicks off the embedded CookieRadar scan (via `submitWebsiteUrl`), then
 * advances. The scan runs in the background; its progress is shown by the
 * floating progress chip and its results pre-fill later service questions.
 * The step is optional — the user may skip it.
 */
export function UrlStep(props: Props) {
  const { step } = props;
  const { question } = step;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const initialUrl = typeof question.value === 'string' ? question.value : '';
  const [url, setUrl] = useState<string>(initialUrl);
  const [submit, { loading }] = useSubmitWebsiteUrlMutation();

  const trimmed = url.trim();
  const isDisabled = trimmed.length === 0 || loading;

  async function onNext() {
    if (trimmed.length === 0) return;
    // Be lenient: prepend https:// when the user omits the scheme. The backend
    // normalises again, but doing it here keeps the displayed value tidy.
    const normalised = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      await submit({ variables: { input: { url: normalised } } });
      props.onAdvance(step.id);
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? t('components.block.quest.scan.url.error'), {
        variant: 'error',
        stack: e?.stack,
      });
    }
  }

  function onSkip() {
    props.onAdvance(step.id);
  }

  return (
    <div className="dtQuestStep flex gap-5">
      <div className="flex-1 hidden md:block" />
      <div className="dtQuestStep__content flex-4">
        <div className="flex gap-5 mb-5">
          <div className="flex-4 text-break">
            <Markdown className="dtQuestStep__question questionTitle" text={question.questionText} />
            <p className="mt-3 text-gray-600">{t('components.block.quest.scan.url.description')}</p>
          </div>
        </div>

        <Input
          autoFocus
          type="text"
          inputMode="url"
          placeholder={t('components.block.quest.scan.url.placeholder')}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onNext();
          }}
        />

        <div className="flex flex-col items-start gap-3 mt-5 mb-5">
          <Button disabled={isDisabled} spinner={loading} onClick={onNext}>
            {t('components.block.quest.scan.url.submit')}
          </Button>
          <button type="button" onClick={onSkip} className="text-sm text-gray-500 underline hover:no-underline">
            {t('components.block.quest.scan.url.skip')}
          </button>
        </div>
      </div>
      <div className="flex-1 hidden md:block" />
    </div>
  );
}
